import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Lock, ExternalLink, Mail, KeyRound } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithToken, user } = useAuth();
  const [mode, setMode] = useState('password'); // 'password' | 'otp-request' | 'otp-verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessError, setAccessError] = useState('');

  useEffect(() => {
    if (user) {
      if (user.role === 'super_admin') {
        navigate('/dashboard', { replace: true });
      } else {
        setAccessError(`Your account (${user.role}) is not authorized for the Super Admin portal. Please use the appropriate portal.`);
      }
    }
  }, [user, navigate]);

  const validateRole = (loggedInUser) => {
    if (loggedInUser.role !== 'super_admin') {
      setAccessError(
        loggedInUser.role === 'admin'
          ? 'This is the Super Admin portal. Sub-admins should use the Sub-Admin portal (subhitch.yourdomain.com).'
          : 'Access denied. This portal is for Super Admins only.'
      );
      localStorage.removeItem('token');
      window.location.reload();
      return false;
    }
    return true;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setAccessError('');
    setLoading(true);
    try {
      const loggedInUser = await login(email, password);
      if (!loggedInUser) throw new Error('Login failed');
      if (!validateRole(loggedInUser)) return;
      toast.success('Welcome to the Admin Portal!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (!accessError) toast.error(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setAccessError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, { email: otpEmail });
      toast.success('OTP sent to your email!');
      setMode('otp-verify');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setAccessError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { email: otpEmail, otp: otpCode });
      const loggedInUser = res.data.user;
      if (!validateRole(loggedInUser)) return;
      loginWithToken(res.data.access_token, loggedInUser);
      toast.success('Welcome to the Admin Portal!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="border-b bg-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="flex items-center gap-2">
            <Logo className="h-7 max-w-[140px] object-contain" />
            <span className="ml-1 text-xs font-semibold text-secondary uppercase tracking-widest">Admin Portal</span>
          </div>
        </div>
        <a href="/" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-site-link">
          <ExternalLink className="w-3.5 h-3.5" /> Main Site
        </a>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-primary" data-testid="portal-title">Super Admin Login</h1>
            <p className="text-muted-foreground text-sm mt-1">Restricted to Super Admin accounts only</p>
          </div>

          {accessError && (
            <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="access-error">{accessError}</div>
          )}

          <div className="bg-white border rounded-xl shadow-sm p-7">
            {/* Mode Toggle */}
            <div className="flex rounded-lg bg-slate-100 p-1 mb-5" data-testid="admin-login-mode-toggle">
              <button type="button" onClick={() => { setMode('password'); setOtpCode(''); setAccessError(''); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${mode === 'password' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'}`}
                data-testid="admin-password-mode-btn">
                <Lock className="w-3.5 h-3.5" /> Password
              </button>
              <button type="button" onClick={() => { setMode('otp-request'); setOtpCode(''); setAccessError(''); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${mode !== 'password' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'}`}
                data-testid="admin-otp-mode-btn">
                <KeyRound className="w-3.5 h-3.5" /> Email OTP
              </button>
            </div>

            {/* Password Login */}
            {mode === 'password' && (
              <form onSubmit={handlePasswordLogin} className="space-y-5" data-testid="admin-login-form">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-2 h-11" data-testid="admin-email-input" placeholder="admin@yourdomain.com" autoComplete="email" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <div className="relative mt-2">
                    <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="h-11 pr-10" data-testid="admin-password-input" placeholder="••••••••" autoComplete="current-password" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 font-semibold" data-testid="admin-login-button">
                  <Shield className="w-4 h-4 mr-2" /> {loading ? 'Authenticating...' : 'Access Admin Panel'}
                </Button>
              </form>
            )}

            {/* OTP Request */}
            {mode === 'otp-request' && (
              <form onSubmit={handleRequestOTP} className="space-y-5" data-testid="admin-otp-request-form">
                <div>
                  <Label htmlFor="otp-email" className="text-sm font-medium">Email Address</Label>
                  <Input id="otp-email" type="email" value={otpEmail} onChange={e => setOtpEmail(e.target.value)} required className="mt-2 h-11" data-testid="admin-otp-email-input" placeholder="admin@yourdomain.com" />
                </div>
                <p className="text-xs text-muted-foreground">A 6-digit verification code will be sent to your email.</p>
                <Button type="submit" disabled={loading} className="w-full h-11 font-semibold" data-testid="admin-otp-send-btn">
                  <Mail className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send OTP'}
                </Button>
              </form>
            )}

            {/* OTP Verify */}
            {mode === 'otp-verify' && (
              <form onSubmit={handleVerifyOTP} className="space-y-5" data-testid="admin-otp-verify-form">
                <p className="text-sm text-center text-muted-foreground">Enter the 6-digit code sent to <strong>{otpEmail}</strong></p>
                <div>
                  <Label htmlFor="otp-code" className="text-sm font-medium">OTP Code</Label>
                  <Input id="otp-code" type="text" maxLength={6} value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))} required className="mt-2 h-11 text-center text-2xl tracking-[0.5em] font-mono" data-testid="admin-otp-code-input" placeholder="------" />
                </div>
                <Button type="submit" disabled={loading || otpCode.length < 6} className="w-full h-11 font-semibold" data-testid="admin-otp-verify-btn">
                  <Shield className="w-4 h-4 mr-2" /> {loading ? 'Verifying...' : 'Verify & Access'}
                </Button>
                <button type="button" onClick={() => setMode('otp-request')} className="w-full text-xs text-secondary hover:underline" data-testid="admin-otp-resend-btn">
                  Didn't receive? Send again
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-5">
            Looking for Sub-Admin access?{' '}
            <a href="/subadmin/login" className="text-secondary hover:underline font-medium" data-testid="subadmin-portal-link">Sub-Admin Portal →</a>
          </p>
        </div>
      </div>

      <footer className="border-t bg-white px-6 py-4 text-center text-xs text-muted-foreground">
        TaskPiece Admin Portal · Unauthorized access is strictly prohibited
      </footer>
    </div>
  );
};

export default AdminLoginPage;
